import React, { useEffect, useState } from "react";
import { fetchVideos } from "../lib/api";
import { Skeleton } from "../components/ui/skeleton";
import { Play, Instagram, Youtube, ExternalLink } from "lucide-react";

const instagramCode = (url) => {
  if (!url) return null;
  const m = url.match(/instagram\.com\/(?:reel|p|tv)\/([A-Za-z0-9_-]+)/);
  return m ? m[1] : null;
};

const youtubeEmbed = (url) => {
  if (!url) return null;
  const yt = url.match(/youtube\.com\/watch\?v=([A-Za-z0-9_-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const sh = url.match(/youtu\.be\/([A-Za-z0-9_-]+)/);
  if (sh) return `https://www.youtube.com/embed/${sh[1]}`;
  const emb = url.match(/youtube\.com\/embed\/([A-Za-z0-9_-]+)/);
  if (emb) return `https://www.youtube.com/embed/${emb[1]}`;
  return null;
};

function InstagramCard({ v }) {
  const code = instagramCode(v.url);
  const canonical = code ? `https://www.instagram.com/reel/${code}/` : v.url;

  return (
    <article
      data-testid={`video-item-${v.id}`}
      className="group flex flex-col rounded-3xl overflow-hidden shadow-sm card-lift border border-border bg-white"
    >
      <a
        href={canonical}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`video-open-${v.id}`}
        className="relative block aspect-[9/16] bg-[hsl(var(--navy))] overflow-hidden"
        aria-label={`Abrir ${v.title} no Instagram`}
      >
        {/* Gradient + grain for visual depth */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,hsl(var(--gold))_0%,transparent_55%)] opacity-[0.18]" />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent_0%,transparent_40%,rgba(0,0,0,0.65)_100%)]" />

        {/* Instagram pill */}
        <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-gold bg-black/30 backdrop-blur px-3 py-1.5 rounded-full border border-white/10">
          <Instagram className="w-3 h-3" /> Reel
        </div>

        {/* Play button */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-20 h-20 rounded-full bg-gold text-navy flex items-center justify-center shadow-[0_10px_40px_-10px_rgba(201,169,97,0.6)] group-hover:scale-110 transition-transform duration-300">
            <Play className="w-8 h-8 ml-1 fill-current" />
          </div>
        </div>

        {/* Title band */}
        <div className="absolute inset-x-0 bottom-0 p-5 text-white">
          {v.call_to_action && (
            <div className="text-[10px] uppercase tracking-[0.3em] text-gold/95">
              {v.call_to_action}
            </div>
          )}
          <h3 className="font-display text-2xl mt-2 leading-tight drop-shadow">
            {v.title}
          </h3>
        </div>
      </a>

      <div className="p-5 bg-white flex-1 flex flex-col">
        {v.impact_phrase && (
          <p className="text-sm text-navy/75 leading-relaxed">{v.impact_phrase}</p>
        )}
        <a
          href={canonical}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-navy/70 hover:text-gold transition w-fit"
          data-testid={`video-cta-${v.id}`}
        >
          Assistir no Instagram <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </div>
    </article>
  );
}

function YouTubeCard({ v, embedUrl }) {
  return (
    <article
      data-testid={`video-item-${v.id}`}
      className="flex flex-col rounded-3xl overflow-hidden shadow-sm card-lift border border-border bg-white"
    >
      <div className="aspect-video w-full bg-navy">
        <iframe
          className="w-full h-full"
          src={embedUrl}
          title={v.title}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
      <div className="p-5 bg-white flex-1 flex flex-col">
        {v.call_to_action && (
          <div className="text-[10px] uppercase tracking-[0.3em] text-gold">
            {v.call_to_action}
          </div>
        )}
        <h3 className="font-display text-xl text-navy mt-2 leading-tight">{v.title}</h3>
        {v.impact_phrase && (
          <p className="text-sm text-navy/70 mt-2">{v.impact_phrase}</p>
        )}
        <a
          href={v.url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-navy/70 hover:text-gold transition w-fit"
          data-testid={`video-cta-${v.id}`}
        >
          <Youtube className="w-3.5 h-3.5" /> Assistir no YouTube
        </a>
      </div>
    </article>
  );
}

export default function Videos() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchVideos()
      .then(setVideos)
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="videos-page" className="relative">
      <div className="max-w-5xl mx-auto section-padding py-8">
        <div className="mb-8">
          <p className="text-[10px] uppercase tracking-[0.3em] text-navy/50">Vídeos</p>
          <h1 className="font-display text-4xl sm:text-5xl text-navy mt-2">Reflexões em vídeo</h1>
          <p className="text-navy/70 mt-3 max-w-2xl">
            Toque em qualquer card para assistir ao reel na íntegra.
          </p>
        </div>

        {loading ? (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="videos-loading">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="aspect-[9/16] rounded-3xl" />
            ))}
          </div>
        ) : videos.length === 0 ? (
          <div
            data-testid="videos-empty-state"
            className="rounded-3xl border border-gold/30 bg-[hsl(var(--parchment))] p-10 sm:p-14 text-center max-w-2xl mx-auto"
          >
            <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Em breve</p>
            <h2 className="font-display text-2xl sm:text-3xl text-navy mt-3 leading-snug">
              Vídeos pastorais chegando em breve
            </h2>
            <p className="mt-3 text-navy/70 leading-relaxed max-w-md mx-auto">
              Estaremos publicando mensagens, reflexões e estudos bíblicos.
              Que Deus continue falando ao seu coração.
            </p>
            <a
              href="/"
              className="mt-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-navy/70 hover:text-gold transition-colors"
              data-testid="videos-empty-back"
            >
              Voltar para o início
            </a>
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {videos.map((v) => {
              const ytEmbed = v.embed_url || youtubeEmbed(v.url);
              const isYT = !!youtubeEmbed(v.url) || !!youtubeEmbed(v.embed_url || "");
              if (isYT) return <YouTubeCard key={v.id} v={v} embedUrl={ytEmbed} />;
              return <InstagramCard key={v.id} v={v} />;
            })}
          </div>
        )}
      </div>
    </div>
  );
}
